import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiClock, FiArrowLeft, FiHome, FiAward, FiGift, FiDownload, FiMail, FiHeart } from 'react-icons/fi';
import { paymentService } from '../services';
import api from '../services/api';
import { LoaderComponent } from '../components/ui';
import { pagaliEvents } from '../utils/pixel';
import { gtagConversions } from '../components/GtagLoader';

const PaymentResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get('status'); // success | failed
  const ref = searchParams.get('ref'); // merchantRef
  const errorMsg = searchParams.get('error');
  const isDigital = searchParams.get('digital') === 'true';
  const digitalEmail = searchParams.get('email') || '';

  // Recuperar info de compra digital guardada antes de redirect (popup bloqueado)
  const pendingDigitalOrderId = sessionStorage.getItem('pendingDigitalOrderId');
  const pendingDigitalEmail = sessionStorage.getItem('pendingDigitalEmail');

  const [paymentData, setPaymentData] = useState<{
    montante?: number;
    referencia?: string;
    status?: string;
    entidade?: string;
    tipoServico?: string;
    tokenRecarga?: string;
    talao?: string;
    pontosGanhos?: number;
    cashback?: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const isDoacao = searchParams.get('origem') === 'doacao' || paymentData?.tipoServico === 'DNT' || (ref || '').startsWith('DNT');

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Resultado do Pagamento');
    if (ref) {
      loadPaymentDetails(ref);
      // Fallback: se for doação, garantir confirmação via API pública
      if (isDoacao || searchParams.get('origem') === 'doacao') {
        api.post('/public/donations/confirm-by-ref', { merchant_ref: ref })
          .then((r: any) => { if (r.data?.success) console.log('[doação] confirmada via fallback'); })
          .catch(() => {});
      }
    } else {
      setLoading(false);
    }
  }, [ref]);

  // Activar acessos digitais quando sabemos que o pagamento foi bem-sucedido
  useEffect(() => {
    const successConfirmed = status === 'success' || paymentData?.status === 'completed';
    if (!successConfirmed) return;

    // Caso 1: orderId guardado no sessionStorage (popup bloqueado → redirect)
    if (pendingDigitalOrderId) {
      api.post(`/public/shop/order/${pendingDigitalOrderId}/activate-digital`).catch(() => {});
      sessionStorage.removeItem('pendingDigitalOrderId');
      sessionStorage.removeItem('pendingDigitalEmail');
    }

    // Meta Pixel — Purchase (valor em CVE, ref como order ID)
    const valueCve = paymentData?.montante ?? 0;
    const orderId = ref ?? paymentData?.referencia ?? 'unknown';
    pagaliEvents.purchaseCompleted(valueCve, orderId);
    gtagConversions.purchase(valueCve, orderId);
  }, [status, paymentData?.status]);

  const loadPaymentDetails = async (merchantRef: string) => {
    try {
      const result = await paymentService.getPaymentStatusByRef(merchantRef);
      if (result.success) {
        setPaymentData({
          montante: result.montante,
          referencia: result.referencia,
          status: result.status,
          entidade: result.entidade,
          tipoServico: result.tipoServico,
          tokenRecarga: result.tokenRecarga,
          talao: result.talao,
          pontosGanhos: (result as any).pontosGanhos,
          cashback: (result as any).cashback,
        });
      }
    } catch (err) {
      console.error('Error loading payment details:', err);
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = status === 'success' || paymentData?.status === 'completed';
  const isFailed = status === 'failed' || paymentData?.status === 'failed';
  const isPending = !isSuccess && !isFailed;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoaderComponent size="lg" text="A verificar pagamento..." />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Status Header */}
        <div className={`p-8 text-center ${
          isSuccess ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
          isFailed ? 'bg-gradient-to-br from-red-400 to-rose-500' :
          'bg-gradient-to-br from-amber-400 to-orange-500'
        }`}>
          <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            {isSuccess ? (
              <FiCheckCircle className="text-4xl text-white" />
            ) : isFailed ? (
              <FiXCircle className="text-4xl text-white" />
            ) : (
              <FiClock className="text-4xl text-white" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {isSuccess
              ? (isDoacao ? 'Donativo Realizado!' : 'Pagamento Realizado!')
              : isFailed ? (isDoacao ? 'Donativo Falhou' : 'Pagamento Falhou')
              : (isDoacao ? 'Donativo Pendente' : 'Pagamento Pendente')}
          </h2>
          <p className="text-white/80 text-sm">
            {isSuccess
              ? (isDoacao ? 'O seu donativo foi confirmado. Obrigado pela sua generosidade! 🙏' : 'O seu pagamento foi processado com sucesso.')
              : isFailed ? (errorMsg ? decodeURIComponent(errorMsg) : (isDoacao ? 'O donativo não foi concluído.' : 'O pagamento não foi concluído.'))
              : (isDoacao ? 'O donativo está a ser processado.' : 'O pagamento está a ser processado.')}
          </p>
        </div>

        {/* Payment Details */}
        <div className="p-6 space-y-4">
          {paymentData?.entidade && (
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500">Entidade</span>
              <span className="text-sm font-semibold text-gray-800">{paymentData.entidade}</span>
            </div>
          )}
          {paymentData?.montante && (
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500">Montante</span>
              <span className="text-lg font-bold text-gray-800">
                {paymentData.montante.toLocaleString('pt-CV')}$
              </span>
            </div>
          )}
          {paymentData?.referencia && (
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500">
                {paymentData.tipoServico === 'RCG' ? 'N.º Telemovel / Contador' : 'Referência'}
              </span>
              <span className="text-sm font-medium text-gray-800">{paymentData.referencia}</span>
            </div>
          )}
          {paymentData?.tokenRecarga && isSuccess && (
            <div className="py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500 block mb-2">Token de Recarga</span>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <span className="text-lg font-mono font-bold text-green-800 tracking-wider select-all">
                  {paymentData.tokenRecarga}
                </span>
                <p className="text-xs text-green-600 mt-1">Introduza este token no seu contador</p>
              </div>
            </div>
          )}
          {paymentData?.talao && isSuccess && (
            <div className="py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500 block mb-2">Talão da Recarga</span>
              <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-700 whitespace-pre-wrap overflow-x-auto">{paymentData.talao}</pre>
            </div>
          )}
          {ref && (
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-500">Transação</span>
              <span className="text-xs font-mono text-gray-600">{ref}</span>
            </div>
          )}

          {/* Banner produto digital */}
          {isSuccess && (isDigital || !!pendingDigitalOrderId) && (
            <div className="mt-2 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FiMail className="text-purple-600 text-lg" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-purple-900 text-sm mb-1">
                    Acesso digital processado com sucesso.
                  </p>
                  {(digitalEmail || pendingDigitalEmail) && (
                    <p className="text-purple-700 text-xs mb-2">
                      Email de acesso preparado para <strong>{digitalEmail || pendingDigitalEmail}</strong>
                    </p>
                  )}
                  <p className="text-purple-600 text-xs">
                    Verifica a tua caixa de entrada e spam. Também podes abrir os teus downloads abaixo.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/meus-downloads')}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                <FiDownload />
                Ver Meus Downloads
              </button>
            </div>
          )}

          {/* Recompensas ganhas */}
          {isSuccess && ((paymentData?.pontosGanhos || 0) > 0 || (paymentData?.cashback || 0) > 0) && (
            <div className="mt-4 p-4 bg-gradient-to-br from-amber-50 to-purple-50 rounded-xl border border-amber-100">
              <div className="flex items-center gap-2 mb-3">
                <FiGift className="text-amber-500 text-xl" />
                <h4 className="font-bold text-gray-800">Recompensas ganhas!</h4>
              </div>
              <div className="space-y-2">
                {(paymentData?.pontosGanhos || 0) > 0 && (
                  <div className="flex items-center justify-between bg-white/60 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <FiAward className="text-amber-600" />
                      <span className="text-sm font-medium text-gray-700">Pontos de fidelidade</span>
                    </div>
                    <span className="font-bold text-amber-600">+{paymentData?.pontosGanhos} pts</span>
                  </div>
                )}
                {(paymentData?.cashback || 0) > 0 && (
                  <div className="flex items-center justify-between bg-white/60 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <FiGift className="text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Cashback na wallet</span>
                    </div>
                    <span className="font-bold text-green-600">+{Number(paymentData?.cashback).toFixed(0)}$ CVE</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 text-center mt-2">
                  🎉 Continua a usar o Pagali e sobe de nível!
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 space-y-3">
            {isDoacao ? (
              <>
                <button
                  onClick={() => navigate('/doacoes')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:from-pink-600 hover:to-rose-600 transition-colors font-medium"
                >
                  <FiHeart />
                  Ver Campanhas de Doação
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  <FiHome />
                  Voltar ao Início
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/payments')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-pagali-primary text-white rounded-xl hover:bg-pagali-primary/90 transition-colors font-medium"
                >
                  <FiArrowLeft />
                  Novo Pagamento
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  <FiHome />
                  Voltar ao Início
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
